import type { BillData } from '../schema/billData';
import type { UserProfile } from '../schema/userProfile';

/**
 * The verified field list and order of the real IRD "Enrollment" (Cash
 * payment) form at https://prize.ird.gov.np/, reverse-engineered from its
 * live deployed JS bundle — see docs/ird-site-analysis.md for the research.
 * This is the ONLY place that list lives; the IRD-assist cheat-sheet UI
 * (src/features/ird/) renders directly from `IRD_FIELD_MAPPINGS`, in order.
 *
 * SAFETY-CRITICAL: the real form also submits a `company_website` field that
 * has no visible label anywhere in its UI — near-certain spam honeypot. It
 * must NEVER appear here. `fieldMapping.test.ts` asserts this permanently.
 */
export interface IrdFieldMapping {
  key: string;
  /** The real form's wire field name, for documentation/traceability only — we never submit to it ourselves. */
  wireName: string;
  label: string;
  order: number;
  source: 'bill' | 'profile';
  getValue: (bill: BillData, profile: UserProfile | null) => string | null;
}

export const IRD_FIELD_MAPPINGS: readonly IrdFieldMapping[] = [
  {
    key: 'billNumber',
    wireName: 'bill_number',
    label: 'Bill Number',
    order: 1,
    source: 'bill',
    getValue: (bill) => bill.billNumber.value,
  },
  {
    key: 'sellerPan',
    wireName: 'seller_pan_no',
    label: 'Seller PAN Number',
    order: 2,
    source: 'bill',
    getValue: (bill) => bill.sellerPan.value,
  },
  {
    key: 'billDateBs',
    wireName: 'bill_date',
    label: 'Bill Date (Bikram Sambat)',
    order: 3,
    source: 'bill',
    getValue: (bill) => bill.billDateBs.value,
  },
  {
    key: 'billDateAd',
    wireName: 'bill_date',
    label: 'Bill Date (AD, if the form shows an English calendar)',
    order: 4,
    source: 'bill',
    getValue: (bill) => bill.billDateAd.value,
  },
  {
    key: 'amount',
    wireName: 'billed_total_amount',
    label: 'Amount',
    order: 5,
    source: 'bill',
    getValue: (bill) => (bill.amount.value === null ? null : bill.amount.value.toFixed(2)),
  },
  {
    key: 'paymentMethod',
    wireName: 'payment_method',
    label: 'Payment Method (select on the real form)',
    order: 6,
    source: 'bill',
    getValue: (bill) => (bill.paymentMethod.value === 'cash' ? 'Cash' : bill.paymentMethod.value),
  },
  {
    key: 'name',
    wireName: 'name',
    label: 'Your Name',
    order: 7,
    source: 'profile',
    getValue: (_bill, profile) => profile?.name ?? null,
  },
  {
    key: 'mobileNumber',
    wireName: 'mobile_number',
    label: 'Your Mobile Number',
    order: 8,
    source: 'profile',
    getValue: (_bill, profile) => profile?.mobileNumber ?? null,
  },
  {
    key: 'address',
    wireName: 'address',
    label: 'Your Address',
    order: 9,
    source: 'profile',
    getValue: (_bill, profile) => profile?.address ?? null,
  },
] as const;

/** Fields the real form submits that we deliberately never populate or suggest — see the safety note above. */
export const IRD_EXCLUDED_HONEYPOT_FIELDS: readonly string[] = ['company_website'];
